import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';

import { INLINE_TYPES } from '../../../../../constants';
import { useInfoConsentWithAppointments } from '../useInfoConsentWithAppointments';
import { checkForIsSignedAllConsents } from '../utils/checkForIsSignedAllConsents';
import { appointmentConsentsModalActions } from '../../../../../redux/healthboard/appointment-consent-modal';
import { preVisitConsentsModalActions } from '../../../../../redux/healthboard/appointment-survey-assignment';
import styles from './index.styl';

export function NotificationConsentView({
  appointment,
  formatMessage,
  isProviderContext,
  defaultMessages = {},
}) {
  const dispatch = useDispatch();

  const code = get(appointment, 'visitType.code');

  const isSignedAllConsents = checkForIsSignedAllConsents(appointment);

  const {
    dateTime,
    allConsents,
    serviceName,
    organizationName,
    notSignedConsent,
    patientUserInfoId,
    notSignedPreVisits,
    isRequiredPreVisitForms,
  } = useInfoConsentWithAppointments(isProviderContext, appointment);

  const providerMessage = formatMessage({
    id: 'appointment.consent.notificationProvider',
  });

  const patientMessage = useMemo(() => {
    if (INLINE_TYPES.includes(code)) {
      return formatMessage({ id: 'appointment.consent.inLineNotification' });
    }

    if (!isSignedAllConsents && isRequiredPreVisitForms) {
      return formatMessage({
        id: 'appointment.surveysAndConsent.notificationProvider',
      });
    }

    if (isRequiredPreVisitForms) {
      return formatMessage({
        id: 'appointment.surveys.notificationProvider',
      });
    }

    if (Object.hasOwn(defaultMessages, 'patient')) {
      return get(defaultMessages, 'patient');
    }

    return formatMessage({
      id: 'appointment.consent.notificationProvider',
    });
  }, [code, isRequiredPreVisitForms, isSignedAllConsents, defaultMessages]);

  const patientMessageLink = useMemo(() => {
    if (INLINE_TYPES.includes(code)) {
      return null;
    }

    if (
      isSignedAllConsents &&
      notSignedPreVisits.length === 0 &&
      isRequiredPreVisitForms
    ) {
      return null;
    }

    if (
      !isSignedAllConsents &&
      isRequiredPreVisitForms &&
      notSignedPreVisits.length > 0
    ) {
      return formatMessage({
        id: 'appointment.consentAndSurvey.notificationPatientLink',
      });
    }

    if (isRequiredPreVisitForms && notSignedPreVisits.length > 0) {
      return formatMessage({
        id: 'appointment.postVisitForm.notificationPatientLink',
      });
    }

    if (Object.hasOwn(defaultMessages, 'patientLink')) {
      return get(defaultMessages, 'patientLink');
    }

    return formatMessage({
      id: 'appointment.consent.notificationPatientLink',
    });
  }, [
    code,
    isRequiredPreVisitForms,
    notSignedPreVisits,
    isSignedAllConsents,
    defaultMessages,
  ]);

  const skipNotification = !isProviderContext && !patientMessage;

  const handleGuardForSigningConsentOrForms = useCallback(() => {
    if (notSignedConsent) {
      dispatch(
        appointmentConsentsModalActions.open({
          serviceName,
          allConsents,
          organizationName,
          patientUserInfoId,
          startTime: dateTime,
          consent: notSignedConsent,
        }),
      );
      return;
    }

    if (isRequiredPreVisitForms && notSignedPreVisits.length > 0) {
      dispatch(preVisitConsentsModalActions.setSurveys(notSignedPreVisits));
    }
  }, [
    dateTime,
    serviceName,
    allConsents,
    organizationName,
    notSignedConsent,
    patientUserInfoId,
    notSignedPreVisits,
    isRequiredPreVisitForms,
  ]);

  if (skipNotification) {
    return null;
  }

  return (
    <div className={styles.root}>
      {isProviderContext ? (
        providerMessage
      ) : (
        <div className={styles.patientMessage}>
          {patientMessage}
          {patientMessageLink && (
            <p
              className={styles.patientMessageLink}
              onClick={handleGuardForSigningConsentOrForms}
            >
              {patientMessageLink}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

NotificationConsentView.propTypes = {
  formatMessage: PropTypes.func,
  appointment: PropTypes.object,
  isProviderContext: PropTypes.bool,
  defaultMessages: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/VisitConsents/NotificationConsentView/index.jsx